import { ApiError } from '@/src/api/types/generated-types'
import { SourceEnum } from '@features/analytics/enums/sourceEnum'
import { AxiosError } from 'axios'
import type { EnrichedDevice } from '../contexts/LocationsContext'
import { env } from '../env'
import { appColors } from '../theme/foundations/colors'
import { CENTER_CONTENT_PADDING_BOTTOM, HEADER_BAR_HEIGHT, HEADER_BAR_MARGIN_BOTTOM } from '../utils/constants'

export const CACHE_TTL = 30

export const forceDecimalValue = (value: number): string => {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value).replace('€', '')
}

export const isNumber = (x: unknown): x is number => x !== undefined && !Number.isNaN(x)

export const numberFormat = (value: number, maximumFractionDigits = 0, minimumFractionDigits = 0): string => {
  const intlInstance = Intl.NumberFormat('de-DE', { maximumFractionDigits, minimumFractionDigits })
  const ret = intlInstance.format(value)
  if (ret === 'NaN') return '-'
  if (ret === '-0') return '0'
  return ret
}

export const numberFormatUnder1000WithDecimals = (value: number) => {
  const absValue = Math.abs(value)
  if (absValue < 1000) {
    return numberFormat(value, 2, 2)
  } else {
    return numberFormat(value, 0)
  }
}

// TODO: Unify a criteria and leave one function for formating this
export const numberFormatUnder10WithDecimals = (value: number, numOfDecimals: number) => {
  const absValue = Math.abs(value)
  if (absValue < 1) {
    return numberFormat(value, 2)
  } else if (absValue < 10) {
    return numberFormat(value, numOfDecimals)
  } else {
    return numberFormat(value, 0)
  }
}

export const formatNumberWithOneOrTwoDecimals = (value: number) => {
  const absValue = Math.abs(value)
  if (absValue < 1) {
    return numberFormat(value, 2)
  } else if (absValue < 10) {
    return numberFormat(value, 1)
  } else {
    return numberFormat(value, 0)
  }
}

export const formatDate = (date: string | number | Date): string => {
  const intlInstance = new Intl.DateTimeFormat('DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Vienna' })
  return intlInstance.format(date instanceof Date ? date : new Date(date))
}

export const formatDateTimeWithUhr = (date: string | number | Date): string => {
  const intlInstance = new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
  return intlInstance.format(date instanceof Date ? date : new Date(date)) + ' Uhr'
}

export const formatNumberCompact = (
  value: number,
  options: {
    maximumFractionDigits?: number
    compactThreshold?: number
    decimalStrategy?: 'auto' | 'fixed' | 'adaptive'
  } = {}
): string => {
  const { maximumFractionDigits = 0, compactThreshold = 1000, decimalStrategy = 'fixed' } = options

  if (!isNumber(value)) return '-'
  if (value === 0) return '0'

  const absValue = Math.abs(value)
  const sign = value < 0 ? '-' : ''

  if (absValue < compactThreshold) {
    let decimals = maximumFractionDigits

    if (decimalStrategy === 'adaptive') {
      decimals = absValue < 1 ? 2 : absValue < 10 ? 1 : 0
    } else if (decimalStrategy === 'auto') {
      decimals = absValue < 1 ? 2 : absValue < 10 ? maximumFractionDigits : 0
    }

    return numberFormat(value, decimals)
  }

  let compactValue: number
  let suffix: string

  if (absValue >= 1_000_000_000) {
    compactValue = absValue / 1_000_000_000
    suffix = 'B'
  } else if (absValue >= 1_000_000) {
    compactValue = absValue / 1_000_000
    suffix = 'M'
  } else {
    compactValue = absValue / 1_000
    suffix = 'k'
  }

  const decimals = compactValue < 10 ? 1 : 0
  const formattedCompact = numberFormat(compactValue, decimals)

  return `${sign}${formattedCompact}${suffix}`
}
export const isProduction = () => window.location.hostname === 'app.ecoplanet.tech' || window.location.hostname === 'cloud.ecoplanet.tech'
export const isReleaseCandidate = () =>
  window.location.hostname === 'rc.ecoplanet.tech' || window.location.hostname === 'rc.cloud.ecoplanet.tech'
export const isBase = () => window.location.hostname === 'base.ecoplanet.tech' || window.location.hostname === 'base.cloud.ecoplanet.tech'
export const isLocal = () => window.location.hostname === 'localhost'

export const getSocketIoConfig = () => {
  return {
    baseUrl: env.socketUrl ?? getApiUrl(),
    path: env.socketPath,
  }
}

export const getApiUrl = () => {
  // adapt VITE_API_BASE_URL in your .env to point to a selected api version.
  if (env.baseUrl) {
    return env.baseUrl
  }

  if (isProduction()) {
    return 'https://api-new.ecoplanet.tech'
  } else if (isReleaseCandidate()) {
    return 'https://api-new-rc.ecoplanet.tech'
  } else if (isLocal()) {
    return `http://localhost:${env.apiPort}`
  } else {
    return 'https://api-new-staging.ecoplanet.tech'
  }
}

export const getProcurementUrl = () => {
  if (env.procurementUrl) {
    return env.procurementUrl
  }

  if (isProduction()) {
    return 'https://cloud.ecoplanet.tech'
  } else if (isReleaseCandidate()) {
    return 'https://rc.cloud.ecoplanet.tech'
  } else if (isLocal()) {
    return `http://localhost:${env.procurementPort}`
  } else {
    return 'https://base.cloud.ecoplanet.tech'
  }
}

export const getTextWidth = ({ fontWeight, fontFamily, fontSize, text }) => {
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')

  if (context) {
    context.font = `${fontWeight} ${fontSize} ${fontFamily}`

    return context.measureText(text).width
  }
  return -1
}

export const isDemo = companyId => {
  return companyId === '9k1S3NpsR71dyXyjAinI' || companyId === 'fdw0ve3Rj0R9uJonPlXR' || companyId === 'td8aL2hZLPucT28YBiim'
}

export const getNewestAndOldestNonSLPDevices = (
  devices: EnrichedDevice[],
  deviceIds: string[]
): { newestDevice: EnrichedDevice | null; oldestDevice: EnrichedDevice | null } => {
  const filteredDevices =
    devices?.filter(device => deviceIds.includes(device.id) && device.source !== SourceEnum.SLP && device.energyType == 'electricity') ?? []

  let newestDevice: EnrichedDevice | null = null
  let oldestDevice: EnrichedDevice | null = null

  for (const device of filteredDevices) {
    if (device.lastDataAvailableDate && (newestDevice === null || device.lastDataAvailableDate > newestDevice.lastDataAvailableDate)) {
      newestDevice = device
    }
    if (device.firstDataAvailableDate && (oldestDevice === null || device.firstDataAvailableDate < oldestDevice.firstDataAvailableDate)) {
      oldestDevice = device
    }
  }

  return { newestDevice, oldestDevice }
}

export const getCenterContentAreaHeight = ({
  excludeHeaderHeight,
  modifier = '+ 0px',
}: { excludeHeaderHeight?: boolean; modifier?: string } = {}) => {
  if (!modifier) modifier = '0px'
  if (excludeHeaderHeight) return `calc(100vh - ${CENTER_CONTENT_PADDING_BOTTOM} ${modifier})`
  return `calc(100vh - ${HEADER_BAR_HEIGHT} - ${HEADER_BAR_MARGIN_BOTTOM} - ${CENTER_CONTENT_PADDING_BOTTOM} ${modifier}) `
}

export const getRightContentAreaHeight = ({ modifier = '+ 0px' }: { modifier?: string } = {}) => {
  return `calc(100vh - ${HEADER_BAR_HEIGHT} - ${HEADER_BAR_MARGIN_BOTTOM} ${modifier}) `
}

/**
 * Note: Returns max 5 years legends
 */
export const getYearLegendsForGraph = (years: string[]) => {
  const colors = [appColors.primary, appColors.secondary, appColors.violet[200], appColors.teal[500], appColors.gray[400]]
  return years.map((year, index) => ({ text: year, lineColor: colors[index] }))
}

export const startSyncedInterval = func => {
  // Calculate how many milliseconds are left until the next minute
  const now = new Date()
  const delay = (60 - now.getSeconds()) * 1000 - now.getMilliseconds()
  // Execute the function once now, and then again at the start of the next minute
  let intervalId: ReturnType<typeof setInterval>
  func()
  const timeoutId = setTimeout(() => {
    func()
    intervalId = setInterval(func, 1000 * 60)
  }, delay)

  return () => {
    clearTimeout(timeoutId)
    if (intervalId) {
      clearInterval(intervalId)
    }
  }
}

export const sanitizeSeriesId = (seriesId: string) => {
  if (seriesId.startsWith('efficiency_output')) {
    const idParts = seriesId.split('-')
    return idParts[0]
  }

  return seriesId
}

export const isAllLocations = (locations, selectedLocationsIndex) =>
  locations?.[selectedLocationsIndex] ? locations[selectedLocationsIndex].id === 'allLocations' : true

export const extractErrorMessage = (err: unknown) => {
  if (err instanceof ApiError) {
    if (err.body instanceof Object && 'message' in err.body) {
      return err.body.message
    }
    return err.statusText
  }
  if (err instanceof AxiosError) {
    return err.response?.data?.message
  }
  if (err instanceof Error) {
    return err.message
  }
  return JSON.stringify(err)
}

export const isNotNullish = <T,>(input: T): input is NonNullable<typeof input> => Boolean(input)

export const isNullOrUndefined = <T,>(input: T | null | undefined): input is undefined | null => input === undefined || input === null
